import React, {useRef, useMemo} from 'react';

import {TrueViewForProducts} from '@optimaxdev/analytics/desktop/components/trueViewForProducts/trueViewForProducts';
import {productClickEvent} from '@optimaxdev/analytics/desktop/lib/productClickEvent/productClickEvent';
import {MediaQuery} from '@optimaxdev/design-system';
import {ArrowLeft, ArrowRight, Icon} from '@optimaxdev/icons';
import {useQuery} from '@tanstack/react-query';
import classNames from 'classnames';
import {useMediaQuery} from 'react-responsive';
import {Navigation} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import {DY_RETRY_COUNT} from 'constants/dyData';
import {RECOMMENDATION_SOURCE} from 'constants/recommendationSource';
import {sendAnalyticsForRecommendationProducts} from 'libs/recommendation';
import {localStore} from 'libs/storage/storage';
import {createOnSwiper} from 'libs/swiper/createOnSwiper';

import {ProductDataType} from 'types/recommendationType';

import {EmptyCartItem} from '../emptyCartItem';
import {RECOMMENDATION_JUMP_PAGE, RecommendationProductView} from '../recommendationProductView';

import {EmptyCartDySkeleton} from './emptyCartDySkeleton/emptyCartDySkeleton';
import {EmptyCartHeader} from './emptyCartHeader/emptyCartHeader';
import {fetchEmptyCartRecommendations} from './utils/fetchDyApi';

import s from './emptyCartDyHeader.scss';

type PropsType = {
    sendDataLayer?: () => void;
};

const EMPTY_CART_RECOMMENDATION_KEY = 'EMPTY_CART_RECOMMENDATION';

/**
 * Product recommendation for empty cart
 * @returns {React.ReactElement} Rendered react component
 */
export const RecommendationDyHeader: React.FC<PropsType> = ({sendDataLayer}) => {
    const navigationNextRef = useRef(null);
    const navigationPrevRef = useRef(null);
    const isLargeScreen = useMediaQuery(MediaQuery.lg);
    const isMediumScreen = useMediaQuery(MediaQuery.md);

    const {data, isError, isLoading} = useQuery({
        queryFn: fetchEmptyCartRecommendations,
        queryKey: [EMPTY_CART_RECOMMENDATION_KEY],
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: DY_RETRY_COUNT,
    });

    /**
     * Will work when you click on the recommendation
     * @param {string} slotId - id`s slot
     * @param {string} location - location recommendation
     */
    const recommendationItemClick = (
        slotId: string,
        location: string,
        productData: ProductDataType & {sku: string},
    ) => {
        localStore.set(RECOMMENDATION_JUMP_PAGE, location);
        if (sendDataLayer) sendDataLayer();
        data?.sendDyEngagement(slotId);

        productClickEvent([productData], {
            category: 'Empty Cart - Recommendations',
            dataSource: 'DY',
            list: 'Empty Cart - Recommendations',
        });
    };

    const swiperSlideMarginDependScreen = useMemo(() => {
        if (isLargeScreen) {
            return 14;
        } else if (isMediumScreen) {
            return 15;
        }
        return 13;
    }, [isLargeScreen, isMediumScreen]);

    if (isError || data?.isReceivedDataEmpty) return null;

    const title = data?.custom?.value ?? 'Recommended For You';

    return (
        <div>
            <EmptyCartHeader title={title} />
            {isLoading ? (
                <EmptyCartDySkeleton />
            ) : (
                <RecommendationProductView customLocation="Empty Cart - Recommendations">
                    {({currentLocation, isLastElement, onProductView, productViewList}) => (
                        <TrueViewForProducts
                            sendAnalytics={() =>
                                sendAnalyticsForRecommendationProducts(
                                    productViewList.filter(Boolean),
                                    {
                                        category: currentLocation,
                                        strategy: {
                                            strategyID: data?.custom?.analytics?.strategyName || '',
                                            strategySource: RECOMMENDATION_SOURCE.DY,
                                        },
                                    },
                                )
                            }
                            isLastProduct={isLastElement}
                            productsList={productViewList}
                            setProducts={onProductView}
                            waitTime={30000}
                        >
                            <div className={s.container}>
                                <button
                                    aria-label="Next slide"
                                    className={classNames(s.btn, s.next)}
                                    ref={navigationNextRef}
                                    type="button"
                                >
                                    <Icon className={s.arrow} icon={ArrowRight} strokeWidth={2} />
                                </button>
                                <button
                                    aria-label="Previous slide"
                                    className={classNames(s.btn, s.prev)}
                                    ref={navigationPrevRef}
                                    type="button"
                                >
                                    <Icon className={s.arrow} icon={ArrowLeft} strokeWidth={2} />
                                </button>
                                <Swiper
                                    modules={[Navigation]}
                                    onInit={createOnSwiper(navigationNextRef, navigationPrevRef)}
                                    slidesPerGroup={3}
                                    slidesPerView={3}
                                    spaceBetween={swiperSlideMarginDependScreen}
                                    speed={300}
                                    tag="ul"
                                >
                                    {data?.data?.map(({productData, sku, slotId}, index) => (
                                        <SwiperSlide key={productData.product_id} tag="li">
                                            <EmptyCartItem
                                                onClick={() =>
                                                    recommendationItemClick(
                                                        slotId,
                                                        currentLocation,
                                                        {
                                                            ...productData,
                                                            sku,
                                                        },
                                                    )
                                                }
                                                isLastElement={data?.data?.length === index + 1}
                                                onProductView={onProductView}
                                                productData={productData}
                                                sku={sku}
                                            />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </TrueViewForProducts>
                    )}
                </RecommendationProductView>
            )}
        </div>
    );
};
